import { z } from 'zod';

import { CursorPaginationMetaSchema, PaginationSchema } from '../../pagination.js';
import { PermissionCatalogNodeSchema } from '../platform/profile-templates/shared.schemas.js';

export const ListOrganizationAccessUsersQuerySchema = PaginationSchema.extend({
  q: z.string().optional(),
});

export type ListOrganizationAccessUsersQuery = z.infer<
  typeof ListOrganizationAccessUsersQuerySchema
>;

export const OrganizationAccessUserItemSchema = z.object({
  userAccessId: z.uuid().nullable(),
  userId: z.uuid().nullable(),
  inviteId: z.uuid().nullable(),
  email: z.email(),
  fullName: z.string(),
  status: z.enum(['invited', 'active', 'inactive', 'blocked']),
  scopeType: z.enum(['TENANT', 'PLACE']),
  profileName: z.string(),
  permissionGroupId: z.uuid(),
  placeName: z.string().nullable(),
});

export type OrganizationAccessUserItem = z.infer<typeof OrganizationAccessUserItemSchema>;

export const ListOrganizationAccessUsersResponseSchema = z.object({
  items: z.array(OrganizationAccessUserItemSchema),
  pagination: CursorPaginationMetaSchema,
});

export type ListOrganizationAccessUsersResponse = z.infer<
  typeof ListOrganizationAccessUsersResponseSchema
>;

export const UpdateOrganizationUserAccessBodySchema = z
  .object({
    permissionGroupId: z.uuid(),
    scopeType: z.enum(['TENANT', 'PLACE']),
    placeId: z.uuid().optional(),
  })
  .refine((value) => value.scopeType === 'TENANT' || value.placeId !== undefined, {
    message: 'placeId is required when scopeType is PLACE',
    path: ['placeId'],
  });

export type UpdateOrganizationUserAccessBody = z.infer<
  typeof UpdateOrganizationUserAccessBodySchema
>;

export const UpdateOrganizationUserAccessResponseSchema = z.object({
  userAccessId: z.uuid(),
});

export type UpdateOrganizationUserAccessResponse = z.infer<
  typeof UpdateOrganizationUserAccessResponseSchema
>;

export const OrganizationAccessIdParamsSchema = z.object({
  userAccessId: z.uuid(),
});

export const OrganizationProfileParamsSchema = z.object({
  permissionGroupId: z.uuid(),
});

export const OrganizationActivePlaceSchema = z.object({
  id: z.uuid(),
  name: z.string(),
});

export const OrganizationProfileListItemSchema = z.object({
  id: z.uuid(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  scopeType: z.literal('TENANT'),
  active: z.literal(true),
  permissionCount: z.number().int().nonnegative(),
  permissionsCustomizedByOrganization: z.boolean(),
  createdAt: z.string(),
});

export type OrganizationProfileListItem = z.infer<typeof OrganizationProfileListItemSchema>;

export const ListOrganizationProfilesResponseSchema = z.object({
  items: z.array(OrganizationProfileListItemSchema),
  activePlaces: z.array(OrganizationActivePlaceSchema),
});

export type ListOrganizationProfilesResponse = z.infer<
  typeof ListOrganizationProfilesResponseSchema
>;

export const OrganizationProfileDetailSchema = OrganizationProfileListItemSchema.extend({
  permissionSlugs: z.array(z.string()),
});

export const GetOrganizationProfileResponseSchema = OrganizationProfileDetailSchema.extend({
  catalog: z.array(PermissionCatalogNodeSchema),
});

export type GetOrganizationProfileResponse = z.infer<typeof GetOrganizationProfileResponseSchema>;

export const UpdateOrganizationProfileBodySchema = z
  .object({
    name: z.string().min(1).optional(),
    description: z.string().nullable().optional(),
    permissionSlugs: z.array(z.string().min(1)).min(1).optional(),
  })
  .refine(
    (value) =>
      value.name !== undefined ||
      value.description !== undefined ||
      value.permissionSlugs !== undefined,
    { message: 'At least one field is required' },
  );

export type UpdateOrganizationProfileBody = z.infer<typeof UpdateOrganizationProfileBodySchema>;

export const UpdateOrganizationProfileResponseSchema = OrganizationProfileDetailSchema;

export type UpdateOrganizationProfileResponse = z.infer<
  typeof UpdateOrganizationProfileResponseSchema
>;
